import { useState } from 'react';
import { photoPreviewUrl, type Photo } from '../lib/api';
import { PlayIcon } from './Icons';

/**
 * Square thumbnail grid, grouped by day.
 *
 * Thumbnails are lazily loaded and fade in, so scrolling a long feed on a
 * phone does not stall on a wall of simultaneous requests.
 */
export function PhotoGrid({
  photos,
  onSelect,
}: {
  photos: Photo[];
  onSelect: (photo: Photo, index: number) => void;
}) {
  const groups = groupByDay(photos);

  return (
    <>
      {groups.map((group) => (
        <section key={group.label}>
          <h2 className="day-heading">{group.label}</h2>
          <div className="photo-grid">
            {group.photos.map((photo) => (
              <Tile
                key={photo.fileId || photo.path}
                photo={photo}
                onSelect={() => onSelect(photo, photos.indexOf(photo))}
              />
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

function Tile({ photo, onSelect }: { photo: Photo; onSelect: () => void }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <button type="button" className="photo-tile" onClick={onSelect}>
      <img
        src={photoPreviewUrl(photo, 400)}
        alt={photo.name}
        loading="lazy"
        decoding="async"
        className={loaded ? 'loaded' : ''}
        onLoad={() => setLoaded(true)}
        // A broken preview should still leave a tappable tile, not a gap.
        onError={() => setLoaded(true)}
      />
      {photo.isVideo && <PlayIcon className="video-badge" />}
    </button>
  );
}

interface DayGroup {
  label: string;
  photos: Photo[];
}

/** Group into Today / Yesterday / a readable date, newest first. */
function groupByDay(photos: Photo[]): DayGroup[] {
  const groups = new Map<string, Photo[]>();

  for (const photo of photos) {
    const key = new Date(photo.lastModified).toDateString();
    const existing = groups.get(key);
    if (existing) existing.push(photo);
    else groups.set(key, [photo]);
  }

  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86_400_000).toDateString();

  return [...groups.entries()].map(([key, items]) => {
    let label: string;
    if (key === today) label = 'Today';
    else if (key === yesterday) label = 'Yesterday';
    else {
      const date = new Date(key);
      const sameYear = date.getFullYear() === new Date().getFullYear();
      label = date.toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        ...(sameYear ? {} : { year: 'numeric' }),
      });
    }
    return { label, photos: items };
  });
}
